import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import jsPDF from "jspdf";

import socket from "../tools/socke";
import regresaBienFecha from "../tools/regresaBienFecha";

// ============================================================
// COMPONENTE
// ============================================================

const AvisosInternosPorFecha = ({ fecha, sede }) => {
  const [avisos, setAvisos] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [verListado, setVerListado] = useState(true);
  const [busqueda, setBusqueda] = useState("");

  // ============================================================
  // FECHA DE HOY YYYY-MM-DD
  // ============================================================

  const getTodayFormatted = () => {
    const hoy = new Date();

    const yyyy = hoy.getFullYear();
    const mm = String(
      hoy.getMonth() + 1
    ).padStart(2, "0");

    const dd = String(
      hoy.getDate()
    ).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  };

  // ============================================================
  // TRAER AVISOS
  // ============================================================

  const fetchAvisos = async (
    showLoading = true
  ) => {
    if (showLoading) {
      setLoading(true);
    }

    try {
      const fechaConsulta =
        fecha || getTodayFormatted();

      console.log("==================================");
      console.log("📤 CONSULTANDO INTERNOS");
      console.log({
        fecha: fechaConsulta,
        sede,
      });
      console.log("==================================");

      const response = await axios.post(
        "https://wwwa.isepsantafe.net.ar/expedientes/NCadetes/obtenerAvisosPorFecha",
        {
          fecha: fechaConsulta,
        }
      );

      console.log(
        "📥 RESPUESTA INTERNOS:",
        response.data
      );

      setAvisos(response.data);
      setError("");
    } catch (err) {
      console.error(
        "❌ ERROR OBTENIENDO INTERNOS:",
        err
      );

      if (
        err.response?.data?.error
      ) {
        setError(
          err.response.data.error
        );
      } else {
        setError(
          "Error al conectar con el servidor."
        );
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // ============================================================
  // EFFECT + SOCKET
  // ============================================================

  useEffect(() => {
    fetchAvisos();

    const handleActualizaInternos = () => {
      console.log(
        "🔄 Actualizando internos por socket"
      );

      fetchAvisos(false);
    };

    socket.on(
      "ActualizaInternos",
      handleActualizaInternos
    );

    return () => {
      socket.off(
        "ActualizaInternos",
        handleActualizaInternos
      );
    };
  }, [fecha, sede]);

  // ============================================================
  // FILTRAR POR SEDE
  // ============================================================

  const avisosFiltradosPorSede =
    useMemo(() => {
      if (
        !avisos ||
        !avisos.avisosPorSede
      ) {
        return {};
      }

      if (!sede) {
        return avisos.avisosPorSede;
      }

      const avisosDeSede =
        avisos.avisosPorSede[sede];

      if (!avisosDeSede) {
        return {};
      }

      return {
        [sede]: avisosDeSede,
      };
    }, [avisos, sede]);

  // ============================================================
  // AGRUPAR POR PROMOCIÓN
  // ============================================================

  const agruparPorPromocion = (
    lista = []
  ) => {
    return lista.reduce(
      (agrupados, item) => {
        const promo =
          item?.cadete?.promocion ||
          "Sin promoción";

        if (!agrupados[promo]) {
          agrupados[promo] = [];
        }

        agrupados[promo].push(
          item
        );

        return agrupados;
      },
      {}
    );
  };

  // ============================================================
  // FILTRAR POR BUSCADOR
  // ============================================================

  const filtrarLista = (
    lista = []
  ) => {
    if (!busqueda.trim()) {
      return lista;
    }

    const texto =
      busqueda
        .trim()
        .toLowerCase();

    return lista.filter(
      (item) => {
        const apellido =
          item?.cadete?.cadeteApellido
            ?.toLowerCase() || "";

        const nombre =
          item?.cadete?.cadeteNombre
            ?.toLowerCase() || "";

        const dni = String(
          item?.cadete?.cadeteDni ||
            ""
        ).toLowerCase();

        const promocion =
          String(
            item?.cadete?.promocion ||
              ""
          ).toLowerCase();

        return (
          apellido.includes(texto) ||
          nombre.includes(texto) ||
          dni.includes(texto) ||
          promocion.includes(texto)
        );
      }
    );
  };

  // ============================================================
  // TOTAL INTERNOS
  // ============================================================

  const totalInternos =
    useMemo(() => {
      return Object.values(
        avisosFiltradosPorSede
      ).reduce(
        (total, lista) =>
          total +
          (Array.isArray(lista)
            ? lista.length
            : 0),
        0
      );
    }, [avisosFiltradosPorSede]);

  // ============================================================
  // TOTAL VISIBLES
  // ============================================================

  const totalVisibles =
    useMemo(() => {
      return Object.values(
        avisosFiltradosPorSede
      ).reduce(
        (total, lista) =>
          total +
          filtrarLista(
            lista
          ).length,
        0
      );
    }, [
      avisosFiltradosPorSede,
      busqueda,
    ]);

  // ============================================================
  // FECHA VISUAL
  // ============================================================

  const fechaVisual =
    fecha || getTodayFormatted();

  // ============================================================
  // ORDENAR POR APELLIDO
  // ============================================================

  const ordenarPorApellido = (
    lista = []
  ) => {
    return [...lista].sort(
      (a, b) => {
        const apellidoA =
          a?.cadete?.cadeteApellido
            ?.trim()
            ?.toUpperCase() ||
          "";

        const apellidoB =
          b?.cadete?.cadeteApellido
            ?.trim()
            ?.toUpperCase() ||
          "";

        return apellidoA.localeCompare(
          apellidoB
        );
      }
    );
  };

  // ============================================================
  // EXPORTAR PDF
  // ============================================================

  const exportarPDF = () => {
    const doc = new jsPDF();

    const sedeTexto =
      sede || "Todas las sedes";

    // ==========================================================
    // TÍTULO
    // ==========================================================

    doc.setFontSize(16);

    doc.text(
      "CADETES INTERNOS",
      10,
      12
    );

    doc.setFontSize(10);

    doc.text(
      `${sedeTexto} - ${regresaBienFecha(
        fechaVisual
      )}`,
      10,
      19
    );

    doc.text(
      `Total: ${totalInternos}`,
      10,
      25
    );

    let yPosition = 35;

    // ==========================================================
    // SEDES
    // ==========================================================

    Object.entries(
      avisosFiltradosPorSede
    ).forEach(
      ([sedeKey, lista]) => {
        const agrupados =
          agruparPorPromocion(
            lista
          );

        Object.entries(
          agrupados
        ).forEach(
          ([
            promo,
            cadetes,
          ]) => {
            // ================================================
            // CONTROL PÁGINA
            // ================================================

            if (
              yPosition > 270
            ) {
              doc.addPage();
              yPosition = 15;
            }

            doc.setFontSize(12);

            doc.text(
              `${promo} (${cadetes.length})`,
              10,
              yPosition
            );

            yPosition += 7;

            ordenarPorApellido(
              cadetes
            ).forEach(
              (
                item,
                index
              ) => {
                if (
                  yPosition > 280
                ) {
                  doc.addPage();
                  yPosition = 15;
                }

                const apellido =
                  item?.cadete
                    ?.cadeteApellido
                    ?.trim() ||
                  "";

                const nombre =
                  item?.cadete
                    ?.cadeteNombre
                    ?.trim() ||
                  "";

                const dni =
                  item?.cadete
                    ?.cadeteDni ||
                  "";

                doc.setFontSize(
                  9
                );

                doc.text(
                  `${index + 1}. ${apellido}, ${nombre} - DNI ${dni}`,
                  12,
                  yPosition
                );

                yPosition += 5;
              }
            );

            yPosition += 5;
          }
        );
      }
    );

    doc.output(
      "dataurlnewwindow"
    );
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div style={styles.loading}>
        Consultando internos...
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (error) {
    return (
      <div style={styles.error}>
        {error}
      </div>
    );
  }

  // ============================================================
  // RETURN
  // ============================================================

  return (
    <div style={styles.container}>

      {/* ====================================================== */}
      {/* CABECERA */}
      {/* ====================================================== */}

      <div style={styles.header}>

        <div>

          <div style={styles.title}>
            Cadetes internos
          </div>

          <div style={styles.subtitle}>
            {regresaBienFecha(
              fechaVisual
            )}
            {sede
              ? ` · ${sede}`
              : ""}
          </div>

        </div>

        <div style={styles.headerActions}>

          <div style={styles.totalBadge}>
            {totalInternos}
          </div>

          <button
            type="button"
            onClick={() =>
              fetchAvisos(false)
            }
            style={styles.iconButton}
            title="Actualizar"
          >
            ↻
          </button>

        </div>

      </div>

      {/* ====================================================== */}
      {/* SIN INTERNOS */}
      {/* ====================================================== */}

      {totalInternos === 0 ? (

        <div style={styles.empty}>
          No hay cadetes internos
          para esta fecha.
        </div>

      ) : (

        <>

          {/* ================================================== */}
          {/* TOOLBAR */}
          {/* ================================================== */}

          <div style={styles.toolbar}>

            <input
              type="text"
              placeholder="Buscar interno..."
              value={busqueda}
              onChange={(e) =>
                setBusqueda(
                  e.target.value
                )
              }
              style={styles.search}
            />

            <button
              type="button"
              onClick={() =>
                setVerListado(
                  !verListado
                )
              }
              style={
                styles.secondaryButton
              }
            >
              {verListado
                ? "Ocultar"
                : "Ver listado"}
            </button>

            <button
              type="button"
              onClick={exportarPDF}
              style={styles.pdfButton}
            >
              PDF
            </button>

          </div>

          {/* ================================================== */}
          {/* RESULTADOS */}
          {/* ================================================== */}

          {busqueda && (

            <div
              style={
                styles.resultCount
              }
            >
              {totalVisibles} de{" "}
              {totalInternos}
            </div>

          )}

          {/* ================================================== */}
          {/* LISTADO */}
          {/* ================================================== */}

          {verListado && (

            <div style={styles.groups}>

              {Object.entries(
                avisosFiltradosPorSede
              ).map(
                ([
                  sedeKey,
                  lista,
                ]) => {
                  const listaFiltrada =
                    filtrarLista(
                      lista
                    );

                  if (
                    listaFiltrada
                      .length === 0
                  ) {
                    return null;
                  }

                  const agrupados =
                    agruparPorPromocion(
                      listaFiltrada
                    );

                  return (

                    <div
                      key={sedeKey}
                    >

                      {!sede && (
                        <div
                          style={
                            styles.sedeTitle
                          }
                        >
                          {sedeKey}
                        </div>
                      )}

                      {Object.entries(
                        agrupados
                      ).map(
                        ([
                          promo,
                          cadetes,
                        ]) => (

                          <div
                            key={promo}
                            style={
                              styles.group
                            }
                          >

                            {/* CABECERA PROMOCIÓN */}

                            <div
                              style={
                                styles.groupHeader
                              }
                            >

                              <span>
                                {promo}
                              </span>

                              <span
                                style={
                                  styles.countBadge
                                }
                              >
                                {
                                  cadetes.length
                                }
                              </span>

                            </div>

                            {/* CADETES */}

                            {ordenarPorApellido(
                              cadetes
                            ).map(
                              (
                                item,
                                index
                              ) => (

                                <div
                                  key={
                                    item.avisoId ||
                                    `${item?.cadete?.cadeteDni}-${index}`
                                  }
                                  style={
                                    styles.row
                                  }
                                >

                                  <div
                                    style={
                                      styles.number
                                    }
                                  >
                                    {index +
                                      1}
                                  </div>

                                  <div
                                    style={
                                      styles.person
                                    }
                                  >

                                    <div
                                      style={
                                        styles.name
                                      }
                                    >

                                      {item?.cadete?.cadeteApellido?.trim() ||
                                        "Sin apellido"}
                                      ,{" "}
                                      {item?.cadete?.cadeteNombre?.trim() ||
                                        "Sin nombre"}

                                    </div>

                                    <div
                                      style={
                                        styles.detail
                                      }
                                    >
                                      DNI{" "}
                                      {item?.cadete?.cadeteDni ||
                                        "-"}
                                    </div>

                                  </div>

                                </div>

                              )
                            )}

                          </div>

                        )
                      )}

                    </div>

                  );
                }
              )}

              {totalVisibles ===
                0 && (

                <div
                  style={
                    styles.empty
                  }
                >
                  No se encontraron
                  cadetes con ese
                  criterio.
                </div>

              )}

            </div>

          )}

        </>

      )}

    </div>
  );
};

export default AvisosInternosPorFecha;

// ============================================================
// ESTILOS
// ============================================================

const styles = {
  container: {
    width: "100%",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },

  title: {
    fontSize: "14px",
    fontWeight: "700",
  },

  subtitle: {
    marginTop: "2px",
    fontSize: "10px",
    color: "#777",
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  totalBadge: {
    minWidth: "30px",
    height: "26px",
    padding: "0 7px",
    borderRadius: "14px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#252a30",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "700",
  },

  iconButton: {
    width: "28px",
    height: "28px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    background: "#fff",
    cursor: "pointer",
    fontSize: "15px",
  },

  toolbar: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    marginBottom: "7px",
  },

  search: {
    flex: "1",
    minWidth: "170px",
    padding: "7px 9px",
    border: "1px solid #d5d8dc",
    borderRadius: "6px",
    fontSize: "12px",
  },

  secondaryButton: {
    padding: "7px 10px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    background: "#f4f5f6",
    cursor: "pointer",
    fontSize: "11px",
  },

  pdfButton: {
    padding: "7px 11px",
    border: "none",
    borderRadius: "6px",
    background: "#b02a37",
    color: "#fff",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "600",
  },

  groups: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
  },

  sedeTitle: {
    marginBottom: "5px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#555",
  },

  group: {
    border:
      "1px solid #e3e5e7",
    borderRadius: "7px",
    overflow: "hidden",
  },

  groupHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 9px",
    background: "#f5f6f7",
    fontSize: "11px",
    fontWeight: "700",
  },

  countBadge: {
    minWidth: "22px",
    padding: "2px 6px",
    borderRadius: "10px",
    background: "#e1e4e7",
    textAlign: "center",
    fontSize: "10px",
  },

  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    borderTop:
      "1px solid #eeeeee",
  },

  number: {
    width: "20px",
    color: "#888",
    fontSize: "10px",
    textAlign: "right",
  },

  person: {
    flex: 1,
    minWidth: 0,
  },

  name: {
    fontSize: "11px",
    fontWeight: "600",
  },

  detail: {
    marginTop: "1px",
    fontSize: "9px",
    color: "#777",
  },

  loading: {
    padding: "10px",
    textAlign: "center",
    fontSize: "11px",
    color: "#777",
  },

  error: {
    padding: "8px",
    background: "#fdecec",
    color: "#a11",
    borderRadius: "6px",
    fontSize: "11px",
  },

  empty: {
    padding: "12px",
    textAlign: "center",
    color: "#777",
    background: "#f7f7f7",
    borderRadius: "6px",
    fontSize: "11px",
  },

  resultCount: {
    marginBottom: "5px",
    textAlign: "right",
    fontSize: "10px",
    color: "#777",
  },
};